import { useEffect, useMemo, useState } from "react";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Grid from "@mui/material/Grid";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import CollectionsBookmarkOutlined from "@mui/icons-material/CollectionsBookmarkOutlined";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {
  LIMITS, altTextFor, canMoveBy, focalPosition, moveBy, moveToPositionOf, sameOrder, visibleIds,
  type FutureCardList, type FutureCardStatus, type FutureCardSummary,
} from "@chronospark/shared";
import CardTile, { type TileAction } from "./CardTile";

type Translate = (key: string, options?: object) => string;

const TABS: FutureCardStatus[] = ["visible", "draft", "archived"];

interface Props {
  list: FutureCardList;
  language: string;
  t: Translate;
  status: FutureCardStatus;
  onStatusChange: (status: FutureCardStatus) => void;
  onOpen: (card: FutureCardSummary) => void;
  onAction: (card: FutureCardSummary, action: TileAction) => void;
  onCreate: () => void;
  /** 並べ替えを保存する（表示中のカード全部の並び）。失敗したら例外を投げる（画面側で元に戻す） */
  onReorder: (ids: number[]) => Promise<void>;
}

function SortableTile(props: {
  card: FutureCardSummary; pinned: boolean; language: string; t: Translate; ids: number[];
  onOpen: Props["onOpen"]; onAction: Props["onAction"]; onMove: (id: number, delta: -1 | 1) => void;
}) {
  const { card, ids, t, onMove, ...rest } = props;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: card.id });
  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} sx={{ height: "100%", opacity: isDragging ? 0.6 : 1 }}>
      <CardTile
        card={card}
        t={t}
        {...rest}
        reorder={{
          canEarlier: canMoveBy(ids, card.id, -1),
          canLater: canMoveBy(ids, card.id, 1),
          onEarlier: () => onMove(card.id, -1),
          onLater: () => onMove(card.id, 1),
          handle: (
            <IconButton
              size="small"
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              aria-label={`${t("futureBoard.list.dragHandle")}: ${card.title}`}
              sx={{ cursor: "grab", touchAction: "none" }}
            >
              <DragIndicatorRounded fontSize="small" />
            </IconButton>
          ),
        }}
      />
    </Box>
  );
}

// 一覧（コラージュ）：今日の一枚と、「わたしの未来」のグリッド。領域は補助表示で、未使用の領域に警告は出さない。
function BoardList({ list, language, t, status, onStatusChange, onOpen, onAction, onCreate, onReorder }: Props) {
  const serverOrder = useMemo(() => visibleIds(list.cards), [list.cards]);
  // 並べ替え中の見た目（保存の応答を待つ間）。サーバーの並びが変わったら、そちらに合わせる。
  const [order, setOrder] = useState<number[]>(serverOrder);
  useEffect(() => setOrder(serverOrder), [serverOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byId = useMemo(() => new Map(list.cards.map(card => [card.id, card])), [list.cards]);
  const total = list.cards.length;
  const atLimit = total >= LIMITS.cards;
  const pinnedCandidate = list.pinned_card_id !== null ? byId.get(list.pinned_card_id) : undefined;
  const pinned = pinnedCandidate?.status === "visible" ? pinnedCandidate : undefined;
  const visibleCards = order.map(id => byId.get(id)).filter((card): card is FutureCardSummary => Boolean(card));
  const shown = status === "visible" ? visibleCards : list.cards.filter(card => card.status === status);

  const commit = async (next: number[]) => {
    if (sameOrder(next, order)) return;
    const previous = order;
    setOrder(next);
    try {
      await onReorder(next);
    } catch {
      setOrder(previous); // 保存できなかったときは、元の並びに戻す（理由は画面側が表示する）
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    void commit(moveToPositionOf(order, Number(event.active.id), Number(event.over.id)));
  };

  const emptyKey = status === "visible" ? "emptyVisible" : status === "draft" ? "emptyDraft" : "emptyArchived";

  return (
    <Box sx={{ color: "text.primary" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: 3.5 }}>
        <Box>
          <Typography component="p" aria-hidden sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: "text.secondary", mb: 0.75 }}>FUTURE BOARD</Typography>
          <Typography variant="h3" component="h1" sx={{ color: "text.primary", fontSize: { xs: 28, sm: 34 }, letterSpacing: "-0.04em" }}>{t("futureBoard.title")}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, lineHeight: 1.8 }}>{t("futureBoard.list.intro")}</Typography>
        </Box>
        <Stack spacing={1} alignItems={{ xs: "flex-start", md: "flex-end" }}>
          <Button variant="contained" onClick={onCreate} disabled={atLimit} startIcon={<AddRounded />} sx={{ px: 2.5, minHeight: 48 }}>
            {t("futureBoard.list.create")}
          </Button>
          <Typography variant="caption" color="text.secondary" aria-live="polite">
            {t("futureBoard.list.count", { count: total, limit: LIMITS.cards })}
          </Typography>
        </Stack>
      </Stack>
      {atLimit && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("futureBoard.list.limitReached", { limit: LIMITS.cards })}</Typography>}

      <Box component="section" aria-labelledby="future-today-heading" sx={{ mb: 4 }}>
        {pinned ? (
          <Card sx={{ borderRadius: 5, overflow: "hidden", boxShadow: theme => `0 12px 32px ${alpha(theme.palette.info.main, 0.12)}` }}>
            <CardActionArea onClick={() => onOpen(pinned)} aria-label={`${t("futureBoard.list.openScene")}: ${pinned.title}`}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.1fr 1fr" }, bgcolor: "info.main" }}>
                <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", minHeight: 260 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "common.white", mb: 2 }}>
                    <AutoAwesomeOutlined sx={{ fontSize: 19 }} />
                    <Typography id="future-today-heading" component="h2" sx={{ color: "inherit", fontSize: 13, fontWeight: 700 }}>{t("futureBoard.list.today")}</Typography>
                  </Stack>
                  <Typography component="h3" sx={{ color: "#ffffff", fontSize: { xs: 23, md: 28 }, lineHeight: 1.6, fontWeight: 700, overflowWrap: "anywhere" }}>{pinned.title}</Typography>
                  {pinned.meaning_excerpt && <Typography variant="body2" sx={{ color: "common.white", mt: 1.5, lineHeight: 1.9, overflowWrap: "anywhere" }}>{pinned.meaning_excerpt}</Typography>}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "#ffffff", mt: 3, borderBottom: "1px solid", borderColor: theme => alpha(theme.palette.common.white, 0.6), pb: 0.5 }}>
                    <Typography sx={{ color: "inherit", fontSize: 13, fontWeight: 700 }}>{t("futureBoard.list.openScene")}</Typography><ArrowForwardRounded sx={{ fontSize: 18 }} />
                  </Stack>
                </Box>
                {pinned.cover?.thumb_url ? (
                  <Box component="img" src={pinned.cover.thumb_url}
                    alt={altTextFor(pinned.cover, language, t("futureBoard.image.defaultAlt"), pinned.cover.alt_text_en)}
                    sx={{ display: "block", width: "100%", height: "100%", minHeight: { xs: 200, sm: 290 }, maxHeight: 370, objectFit: "cover", objectPosition: focalPosition(pinned.cover) }} />
                ) : (
                  <Box aria-hidden sx={{ minHeight: { xs: 160, sm: 290 }, background: theme => `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.light})`, display: "grid", placeItems: "center" }}>
                    <AutoAwesomeOutlined sx={{ color: "#ffffff", fontSize: 72, opacity: 0.8 }} />
                  </Box>
                )}
              </Box>
            </CardActionArea>
          </Card>
        ) : (
          <Box sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper", borderRadius: 5, p: { xs: 3, md: 3.5 }, display: "flex", alignItems: "center", gap: 3 }}>
            <Box aria-hidden sx={{ display: { xs: "none", sm: "grid" }, flexShrink: 0, placeItems: "center", width: 88, height: 88, borderRadius: "50%", bgcolor: theme => alpha(theme.palette.info.main, 0.1), color: theme => theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.main }}><AutoAwesomeOutlined sx={{ fontSize: 34 }} /></Box>
            <Box>
              <Typography id="future-today-heading" component="h2" sx={{ color: "text.primary", fontSize: 16, fontWeight: 700, mb: 0.75 }}>{t("futureBoard.list.today")}</Typography>
              <Typography variant="body2" color="text.primary">{t("futureBoard.list.noTodayHint")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.8 }}>{t("futureBoard.list.chooseTodayHint")}</Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <CollectionsBookmarkOutlined sx={{ fontSize: 21, color: "text.secondary" }} />
        <Typography variant="h6" component="h2" sx={{ color: "text.primary" }}>{t("futureBoard.list.myFuture")}</Typography>
      </Stack>
      <Tabs value={status} onChange={(_, value: FutureCardStatus) => onStatusChange(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t("futureBoard.list.myFuture")}
        sx={{ mb: 2.5, p: 0.5, minHeight: 48, border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", "& .MuiTabs-indicator": { display: "none" }, "& .MuiTabs-flexContainer": { gap: 0.5 }, "& .MuiTab-root": { minHeight: "42px !important", minWidth: { xs: "100px !important", sm: "150px !important" }, flex: "1 0 auto", px: 1.5, color: "text.secondary", borderRadius: 2, fontSize: 13, opacity: 1, "&.Mui-selected": { color: "#ffffff !important", bgcolor: "info.main" }, "&.Mui-focusVisible": { outline: theme => `3px solid ${theme.palette.info.light}`, outlineOffset: -3 } } }}>
        {TABS.map(tab => (
          <Tab key={tab} id={`future-tab-${tab}`} aria-controls={`future-panel-${tab}`} value={tab}
            label={<Stack direction="row" spacing={1} alignItems="center"><span>{t(`futureBoard.tabs.${tab}`)}</span><Box component="span" sx={{ borderRadius: 1, px: 0.75, py: 0.15, bgcolor: status === tab ? "#ffffff24" : "action.hover", fontSize: 11, minWidth: 22 }}>{list.cards.filter(card => card.status === tab).length}</Box></Stack>} />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`future-panel-${status}`} aria-labelledby={`future-tab-${status}`}>

        {status === "visible" && shown.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{t("futureBoard.list.reorderHint")}</Typography>
        )}

        {shown.length === 0 ? (
          <Box sx={{ py: { xs: 5, sm: 7 }, px: 3, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 5, bgcolor: "background.paper" }}>
            <Box aria-hidden sx={{ display: "grid", placeItems: "center", width: 64, height: 64, mx: "auto", mb: 2.5, bgcolor: theme => alpha(theme.palette.info.main, 0.1), color: theme => theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.main, borderRadius: 4 }}><CollectionsBookmarkOutlined sx={{ fontSize: 30 }} /></Box>
            <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, color: "text.primary", mb: 1 }}>{t(`futureBoard.list.${emptyKey}`)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mx: "auto", lineHeight: 1.9, mb: 2.5 }}>{t(`futureBoard.list.${status === "visible" ? "emptyStartHint" : status === "draft" ? "emptyDraftHint" : "emptyArchivedHint"}`)}</Typography>
            {status !== "archived" && <Button variant="contained" startIcon={<AddRounded />} onClick={onCreate} disabled={atLimit}>{t("futureBoard.list.create")}</Button>}
          </Box>
        ) : status === "visible" ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={rectSortingStrategy}>
              <Grid container spacing={2.5}>
                {shown.map(card => (
                  <Grid item xs={12} sm={6} lg={4} key={card.id}>
                    <SortableTile
                      card={card}
                      pinned={card.id === list.pinned_card_id}
                      language={language}
                      t={t}
                      ids={order}
                      onOpen={onOpen}
                      onAction={onAction}
                      onMove={(id, delta) => void commit(moveBy(order, id, delta))}
                    />
                  </Grid>
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        ) : (
          <Grid container spacing={2.5}>
            {shown.map(card => (
              <Grid item xs={12} sm={6} lg={4} key={card.id}>
                <CardTile card={card} pinned={false} language={language} t={t} onOpen={onOpen} onAction={onAction} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}

export default BoardList;
